import { useGeckoNotifications } from 'features/notifications'
import isEqual from 'lodash/isEqual'
import {
  createContext,
  Dispatch,
  FC,
  PropsWithChildren,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  NiiScreeningConfig,
  NiiScreeningTicketProps,
  niiScreeningTicketPropsSchema,
  useSaveTicketNiiScreeningData,
  useTicketNiiScreeningData,
} from './niiScreeningApi'

interface NiiScreeningDataContextValue {
  niiScreening: NiiScreeningTicketProps | undefined
  setNiiScreening: Dispatch<SetStateAction<NiiScreeningTicketProps | undefined>>
  niiConfig: NiiScreeningConfig
  hasChanges: boolean
  doSave: () => Promise<void>
  resetChanges: () => void
  isLoading: boolean
  isSaving: boolean
}

const defaultNiiScreening = niiScreeningTicketPropsSchema.parse({})

const NiiScreeningDataContext = createContext<NiiScreeningDataContextValue>({
  niiScreening: defaultNiiScreening,
  setNiiScreening: () => {},
  niiConfig: {},
  hasChanges: false,
  doSave: async () => {},
  resetChanges: () => {},
  isLoading: false,
  isSaving: false,
})

function useNiiScreeningDataInternal(): {
  niiScreening: NiiScreeningTicketProps | undefined
  setNiiScreening: Dispatch<SetStateAction<NiiScreeningTicketProps | undefined>>
  doSave: () => Promise<void>
  hasChanges: boolean
  resetChanges: () => void
  isLoading: boolean
  isSaving: boolean
} {
  const { isLoading: isLoadingTicketData, niiScreening: ticketNiiScreening } =
    useTicketNiiScreeningData()

  const [niiScreening, setNiiScreening] = useState<
    NiiScreeningTicketProps | undefined
  >(ticketNiiScreening)
  const [isSaving, setIsSaving] = useState(false)

  // Sync local state with ticket data
  useEffect(() => {
    setNiiScreening(ticketNiiScreening)
  }, [ticketNiiScreening])

  const hasChanges = useMemo(
    () => !isEqual(niiScreening, ticketNiiScreening),
    [niiScreening, ticketNiiScreening]
  )

  const { errorNotification } = useGeckoNotifications()
  const saveTicketNiiScreening = useSaveTicketNiiScreeningData()
  const doSave = useCallback(async () => {
    setIsSaving(true)
    try {
      if (niiScreening) {
        await saveTicketNiiScreening(niiScreening)
      } else {
        errorNotification('No NII screening data to save')
      }
    } finally {
      setIsSaving(false)
    }
  }, [errorNotification, niiScreening, saveTicketNiiScreening])

  const resetChanges = useCallback(() => {
    setNiiScreening(ticketNiiScreening)
  }, [ticketNiiScreening])

  return useMemo(
    () => ({
      niiScreening,
      setNiiScreening,
      doSave,
      hasChanges,
      resetChanges,
      isLoading: isLoadingTicketData || isSaving,
      isSaving,
    }),
    [
      niiScreening,
      setNiiScreening,
      doSave,
      hasChanges,
      resetChanges,
      isLoadingTicketData,
      isSaving,
    ]
  )
}

export const NiiScreeningDataProvider: FC<
  PropsWithChildren<{ niiConfig: NiiScreeningConfig }>
> = ({ children, niiConfig }) => {
  const {
    isLoading,
    isSaving,
    niiScreening,
    setNiiScreening,
    hasChanges,
    doSave,
    resetChanges,
  } = useNiiScreeningDataInternal()

  const value = useMemo(
    () => ({
      niiScreening,
      setNiiScreening,
      niiConfig,
      hasChanges,
      doSave,
      resetChanges,
      isSaving,
      isLoading,
    }),
    [
      niiScreening,
      setNiiScreening,
      niiConfig,
      hasChanges,
      doSave,
      resetChanges,
      isSaving,
      isLoading,
    ]
  )

  return (
    <NiiScreeningDataContext.Provider value={value}>
      {children}
    </NiiScreeningDataContext.Provider>
  )
}

export const useNiiScreeningData = () => {
  const context = useContext(NiiScreeningDataContext)
  if (!context) {
    throw new Error(
      'useNiiScreeningData must be used within NiiScreeningDataProvider'
    )
  }
  return context
}
